import { ReactText, useRef, useState } from 'react'
import cx from 'classnames'
import { useSelector } from 'react-redux'

import { ModalPromptProps } from 'hooks/useActionModalPrompt'
import { CreateAccessesActionConfig } from 'stores/Workflows/Types'
import { EmailValue, Value } from 'components/ShareSheet/RecipientSelector/Types'
import { Permission, RootState, SettingsMember, RecipientsSelectorValueType } from 'types'
import { useAuth } from 'hooks/UseAuth'
import { InviteToWorkspace } from 'components/ShareSheet/InviteToWorkspace'
import { useWorkspaceMembers } from 'hooks/useWorkspaceMembers'
import { useWorkspaceChannels } from 'hooks/useWorkspaceChannels'
import { DehydratedChannel, DehydratedEmail, DehydratedUser, useHydrateInvites } from 'hooks/useHydrateInvites'
import { dehydrateInvites } from 'components/ShareSheet/RecipientSelector/dehydrateInvites'
import { Assignee } from 'hooks/useExecuteWorkflowActions'
import { Modal } from 'almanac-ui/Modal'
import { AccessDropdown } from 'components/ShareSheet/Access/AccessDropdown'
import { SharedRecipientSelector } from 'components/ShareSheet/RecipientSelector/SharedRecipientSelector'
import { Button } from 'components/Button'
import { ReactComponent as InviteIcon } from 'assets/icons/invite16.svg'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { LAYERS_HELP_DOC } from 'utils/constants'
import { ContentComposer } from 'components/ShareSheet/ContentComposer/ContentComposer'
import Styles from 'components/ShareSheet/ShareSheet.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props extends ModalPromptProps<Partial<CreateAccessesActionConfig>, { assignee: Assignee }> {}

export const UnpersistedFileModalV2 = ({ onConfirm, assignee, onClose, ...config }: Props) => {
  const htmlMessage = useRef<string>('')
  const contentRef = useRef<any>()
  const openWindowSafely = useOpenWindowSafely()
  const selectorRef = useRef<any>(null)
  const auth = useAuth()
  const isNotSelf = (member: SettingsMember) => auth?.id !== member.id
  const currentWorkspace = useSelector((state: RootState) =>
    state.workspace.workspaces.find((w) => w.id === state.workspace.currentWorkspaceId),
  )
  const currentWorkspaceId = currentWorkspace?.key
  const workspaceMembers = useWorkspaceMembers(currentWorkspaceId, { includeInactive: false })
  const workspaceChannels = useWorkspaceChannels(currentWorkspaceId, true)
  const [recipients, setRecipients] = useState<Value[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [permission, setPermission] = useState<Permission>((config.permission || 'edit') as Permission)
  const [inviteToWorkspace, setInviteToWorkspace] = useState(false)
  const [showInviteConfirmation, setShowInviteConfirmation] = useState(false)
  const { isMobile } = useResponsiveDesign()
  const everyoneGroupAccess = {
    type: RecipientsSelectorValueType.EveryoneAtWorkspace,
    name: !!currentWorkspace ? `Everyone at ${currentWorkspace.name}` : 'Everyone in workspace',
    avatar_url: currentWorkspace?.logo_url || '',
    member_count: workspaceMembers.length,
  }
  const childRows = workspaceMembers.map((m) => {
    return { type: RecipientsSelectorValueType.User, name: m.display_name, avatar_url: m.avatar_url || '' }
  })

  const goToExplainAccessLink = () => {
    openWindowSafely(LAYERS_HELP_DOC)
  }

  const emailInvites = recipients
    .filter(({ type }) => type === RecipientsSelectorValueType.Email)
    .map(({ value }) => value) as string[]

  const { users = [], emails = [], channels = [] } = config

  useHydrateInvites({
    invites: [
      ...(users.map((id) => ({ type: 'User', id })) as DehydratedUser[]),
      ...(channels.map((id) => ({ type: 'Channel', id })) as DehydratedChannel[]),
      ...(emails.map((email) => ({ type: 'Email', email })) as DehydratedEmail[]),
    ],
    callback: setRecipients,
  })

  const handleSubmit = () => {
    if (emailInvites.length > 0) {
      setShowInviteConfirmation(true)
      return
    }

    handleConfirm()
  }

  const handleConfirm = () => {
    setIsSubmitting(true)

    onConfirm({
      ...dehydrateInvites(recipients),
      permission,
      html_message: htmlMessage.current,
      add_to_workspace: inviteToWorkspace,
    })
    setIsSubmitting(false)
    onClose()
  }

  if (showInviteConfirmation) {
    return (
      <Modal.Root size={isMobile ? 'xs' : 'sm'}>
        <InviteToWorkspace
          newRecipients={recipients.filter((r) => r.type === RecipientsSelectorValueType.Email) as EmailValue[]}
          goBack={() => setShowInviteConfirmation(false)}
          setInviteToWorkspace={setInviteToWorkspace}
          inviteToWorkspace={inviteToWorkspace}
          onSubmit={handleConfirm}
          isSubmitting={isSubmitting}
        />
      </Modal.Root>
    )
  }

  return (
    <Modal.Root onPressOutside={onClose}>
      <Modal.Header title={<div className={Styles.title}>Invite collaborators</div>} onClose={onClose} />
      <Modal.Content>
        <div
          className={cx(Styles.recipientSelector, Styles.alignTop)}
          onClick={() => {
            selectorRef?.current?.focus()
          }}
        >
          <span className={Styles.toLabel}>To:</span>
          <SharedRecipientSelector
            ref={selectorRef}
            placeholder="Add people, groups, or emails"
            sourceData={{
              users: workspaceMembers.filter(isNotSelf),
              channels: workspaceChannels,
            }}
            recipients={recipients}
            className={Styles.recipientSelectorContainer}
            onChange={setRecipients}
            topGroups={[
              {
                group: everyoneGroupAccess,
                childRows: childRows,
              },
            ]}
          />
          <AccessDropdown
            options={[Permission.Edit, Permission.Comment, Permission.Read]}
            currentPermission={permission}
            handleClick={(permission: ReactText) => setPermission(permission as Permission)}
          />
        </div>
        {permission !== Permission.Edit && (
          <div className={Styles.helpText}>
            <span>Read/comment members can suggest changes with a layer.</span>
            <Button onPress={goToExplainAccessLink} variant="tertiaryGreen">
              Learn more
            </Button>
          </div>
        )}
        <div className={Styles.messageWrapper}>
          <ContentComposer
            ref={contentRef}
            content={config.html_message || ''}
            onChange={(message) => (htmlMessage.current = message)}
          />
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className={Styles.buttonContainer}>
          <Modal.FooterButton frontIcon={InviteIcon} size="lg" onPress={handleSubmit} isDisabled={isSubmitting}>
            Invite
          </Modal.FooterButton>
        </div>
      </Modal.Footer>
    </Modal.Root>
  )
}
